import React from 'react';
import { Plus, CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { INCOME_SOURCES } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const SALES_SOURCES = ['chicken_sales', 'garden_produce', 'masonry_sales', 'maize_sales'];

export const IncomeFormDialog = ({
  open, onOpenChange, formData, setFormData, onSubmit,
  editingId, projects, onReceiptUpload, onTriggerClick,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogTrigger asChild>
      <Button className="btn-press" data-testid="add-income-btn" onClick={onTriggerClick}>
        <Plus className="mr-2 h-4 w-4" />
        Add Income
      </Button>
    </DialogTrigger>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-heading">{editingId ? 'Edit Income' : 'Record Income'}</DialogTitle>
        <DialogDescription>{editingId ? 'Update this income entry' : 'Add a new income entry — fields update based on source'}</DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-3">
        {/* Source + Date — always shown */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Source *</Label>
            <Select
              value={formData.source}
              onValueChange={(value) => setFormData({ ...formData, source: value })}
              required
            >
              <SelectTrigger data-testid="income-source-select">
                <SelectValue placeholder="Select source" />
              </SelectTrigger>
              <SelectContent>
                {INCOME_SOURCES.map((source) => (
                  <SelectItem key={source.value} value={source.value}>
                    {source.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                  data-testid="income-date-picker"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.date ? format(formData.date, 'PPP') : 'Pick date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar mode="single" selected={formData.date} onSelect={(d) => setFormData({ ...formData, date: d })} initialFocus />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Source-specific fields */}
        {formData.source === 'school_fees' && (
          <div className="border-l-2 border-primary/40 pl-3 space-y-3" data-testid="school-fees-fields">
            <h4 className="font-medium text-sm text-primary">School Fees Details</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Student First Name *</Label>
                <Input value={formData.student_first_name}
                  onChange={(e) => setFormData({ ...formData, student_first_name: e.target.value })}
                  required data-testid="income-student-first" />
              </div>
              <div className="space-y-1.5">
                <Label>Student Last Name *</Label>
                <Input value={formData.student_last_name}
                  onChange={(e) => setFormData({ ...formData, student_last_name: e.target.value })}
                  required data-testid="income-student-last" />
              </div>
              <div className="space-y-1.5">
                <Label>Class</Label>
                <Input value={formData.student_class}
                  onChange={(e) => setFormData({ ...formData, student_class: e.target.value })}
                  placeholder="e.g. P.5 / S.2" data-testid="income-student-class" />
              </div>
              <div className="space-y-1.5">
                <Label>Parent / Guardian</Label>
                <Input value={formData.parent_guardian_name}
                  onChange={(e) => setFormData({ ...formData, parent_guardian_name: e.target.value })}
                  data-testid="income-parent-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Amount Paid (UGX) *</Label>
                <Input type="number" value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required data-testid="income-amount" />
              </div>
              <div className="space-y-1.5">
                <Label>Outstanding Balance (UGX)</Label>
                <Input type="number" value={formData.outstanding_balance}
                  onChange={(e) => setFormData({ ...formData, outstanding_balance: e.target.value })}
                  data-testid="income-outstanding" />
              </div>
            </div>
          </div>
        )}

        {['grants', 'donations'].includes(formData.source) && (
          <div className="border-l-2 border-primary/40 pl-3 space-y-3" data-testid="grant-fields">
            <h4 className="font-medium text-sm text-primary">{formData.source === 'grants' ? 'Grant Details' : 'Donation Currency'}</h4>
            <div className="grid grid-cols-2 gap-3">
              {formData.source === 'grants' && (
              <div className="space-y-1.5 col-span-2">
                <Label>Grant Name *</Label>
                <Input value={formData.grant_name}
                  onChange={(e) => setFormData({ ...formData, grant_name: e.target.value })}
                  required placeholder="e.g. USAID Education 2026"
                  data-testid="income-grant-name" />
              </div>
              )}
              <div className="space-y-1.5">
                <Label>Currency *</Label>
                <Select value={formData.currency}
                  onValueChange={(v) => setFormData({ ...formData, currency: v, exchange_rate: v === 'UGX' ? 1 : formData.exchange_rate })}>
                  <SelectTrigger data-testid="income-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">US Dollars (USD)</SelectItem>
                    <SelectItem value="EUR">Euros (EUR)</SelectItem>
                    <SelectItem value="GBP">UK Pounds (GBP)</SelectItem>
                    <SelectItem value="UGX">Uganda Shillings (UGX)</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Original Amount *</Label>
                <Input type="number" value={formData.original_amount}
                  onChange={(e) => setFormData({ ...formData, original_amount: e.target.value })}
                  required data-testid="income-original-amount" />
              </div>
              <div className="space-y-1.5">
                <Label>Exchange Rate (→ UGX) *</Label>
                <Input type="number" step="0.01" value={formData.exchange_rate}
                  onChange={(e) => setFormData({ ...formData, exchange_rate: e.target.value })}
                  required disabled={formData.currency === 'UGX'}
                  data-testid="income-exchange-rate" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label>Calculated UGX</Label>
                <div className="p-2 rounded-md bg-muted text-lg font-bold text-green-600" data-testid="income-calculated-ugx">
                  UGX {((parseFloat(formData.original_amount) || 0) * (parseFloat(formData.exchange_rate) || 0)).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        )}

        {SALES_SOURCES.includes(formData.source) && (
          <div className="border-l-2 border-primary/40 pl-3 space-y-3" data-testid="sales-fields">
            <h4 className="font-medium text-sm text-primary">Sales Details</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Sales Cycle *</Label>
                <Input value={formData.sales_cycle}
                  onChange={(e) => setFormData({ ...formData, sales_cycle: e.target.value })}
                  placeholder="e.g. May 2026 / Cycle 3"
                  required data-testid="income-sales-cycle" />
              </div>
              <div className="space-y-1.5">
                <Label>Number of Items Sold *</Label>
                <Input type="number" value={formData.items_sold}
                  onChange={(e) => setFormData({ ...formData, items_sold: e.target.value })}
                  required data-testid="income-items-sold" />
              </div>
              <div className="space-y-1.5">
                <Label>Payment Amount (UGX) *</Label>
                <Input type="number" value={formData.payment_amount}
                  onChange={(e) => setFormData({ ...formData, payment_amount: e.target.value, amount: e.target.value })}
                  required data-testid="income-payment-amount" />
              </div>
              <div className="space-y-1.5">
                <Label>Outstanding Balance (UGX)</Label>
                <Input type="number" value={formData.outstanding_balance}
                  onChange={(e) => setFormData({ ...formData, outstanding_balance: e.target.value })}
                  data-testid="income-sales-outstanding" />
              </div>
            </div>
          </div>
        )}

        {/* Fallback amount for other sources */}
        {formData.source && !['school_fees', 'grants', ...SALES_SOURCES].includes(formData.source) && (
          <div className="space-y-1.5">
            <Label>Amount (UGX) *</Label>
            <Input type="number" value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required data-testid="income-amount" />
          </div>
        )}

        <div className="space-y-1.5">
          <Label>Description (Optional)</Label>
          <Input value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            data-testid="income-description" />
        </div>

        {/* Program / payment method / reference */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label>Program {['grants', 'donations'].includes(formData.source) && <span className="text-red-500">*</span>}</Label>
            <Select value={formData.program} onValueChange={(v) => setFormData({ ...formData, program: v })}>
              <SelectTrigger data-testid="income-program-select">
                <SelectValue placeholder={['grants', 'donations'].includes(formData.source) ? 'Required — choose program' : 'Auto by source'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="school">School Program</SelectItem>
                <SelectItem value="women">Women Empowerment</SelectItem>
                <SelectItem value="general">General / Org</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Payment Method</Label>
            <Select value={formData.payment_method} onValueChange={(v) => setFormData({ ...formData, payment_method: v })}>
              <SelectTrigger data-testid="income-payment-method"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="mobile_money">Mobile Money</SelectItem>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="cheque">Cheque</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Reference / Receipt No.</Label>
            <Input value={formData.reference_number}
              onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
              placeholder="e.g. RCT-0042" data-testid="income-reference" />
          </div>
        </div>

        {/* Designation / Donor / Funding source */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label>Designated For</Label>
            <Select value={formData.designation || ''} onValueChange={(v) => setFormData({ ...formData, designation: v })}>
              <SelectTrigger data-testid="income-designation-select"><SelectValue placeholder="Auto by program" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="general_operations">General Operations</SelectItem>
                <SelectItem value="education">Education</SelectItem>
                <SelectItem value="women_project">Women Project</SelectItem>
                <SelectItem value="school_farm">School Farm</SelectItem>
                <SelectItem value="construction">Construction</SelectItem>
                <SelectItem value="sustainable_projects">Sustainable Projects</SelectItem>
                <SelectItem value="sponsorship">Sponsorship</SelectItem>
                <SelectItem value="other">Other Project / Program</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{['donations', 'grants', 'sponsorship'].includes(formData.source) ? 'Donor' : 'Customer / Buyer'}</Label>
            <Input value={formData.donor || ''}
              onChange={(e) => setFormData({ ...formData, donor: e.target.value })}
              placeholder="Who paid / donated" data-testid="income-donor" />
          </div>
          <div className="space-y-1.5">
            <Label>Funding Source</Label>
            <Input value={formData.funding_source || ''}
              onChange={(e) => setFormData({ ...formData, funding_source: e.target.value })}
              placeholder="e.g. NGO Grant Q2" data-testid="income-funding-source" />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Receipt / Voucher / Document (Optional)</Label>
          <div className="flex items-center gap-2">
            <Input type="file" accept="image/*,application/pdf" onChange={onReceiptUpload} data-testid="income-receipt-upload" />
            {formData.receipt_image && (
              <Badge variant="outline" className="whitespace-nowrap">
                {formData.document_name || 'Attached'}
              </Badge>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Related Project (Optional)</Label>
          <Select
            value={formData.related_project}
            onValueChange={(value) => setFormData({ ...formData, related_project: value === '__none__' ? '' : value })}
          >
            <SelectTrigger data-testid="income-related-project">
              <SelectValue placeholder="Link to a project (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" data-testid="income-submit-btn">
            {editingId ? 'Update Income' : 'Save Income'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);
